import endpoints from "../config/endpoints.json";
import connection_production from "../config/connection-production.json";
import connection_local from "../config/connection-local.json";
import connection_development from "../config/connection-development.json";
import axios from "axios";
import type { ApiResponse } from "../types";
import { buildVoiceSampleUrl } from "../utilization/VoiceSampleUtilization";

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
      ? connection_development
      : process.env.REACT_APP_NODE_ENV === "production"
        ? connection_production
        : connection_local;

/** Voice sample of a market voice, spoken by the vendor the mascot chat is routed to. */
export const getVoiceSampleUrl = (voiceName: string, language?: string) =>
  buildVoiceSampleUrl(connection.base.ai.url, voiceName, language);

/**
 * Activity Template Solver
 */
export const solveActivityWithAIOnlyImageWithToken = async (
  solveWithAI,
  token,
): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.post(
      `${connection.base.ai.url}${endpoints.ai["activity-template-solver"].context}${endpoints.ai["activity-template-solver"]["only-image"].context}`,
      solveWithAI,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Chat streams. One endpoint per feature — the backend picks which model answers, so the
 * client neither names nor selects one. The POST opens the stream and returns a `streamId`;
 * the SSE GET is `<streamUrl>/{streamId}`.
 */
export const getChatActivityTemplateAsStreamUrl = () => {
  return `${connection.base.ai.url}${endpoints.ai["activity-template-chat"].context}${endpoints.ai["activity-template-chat"]["as-stream"].context}`;
};

export const chatActivityTemplateAsStream = async (
  chatParams,
): Promise<ApiResponse> => {
  const url = getChatActivityTemplateAsStreamUrl();
  try {
    const response = await axios.post(url, { ...chatParams, supportsDiagram: localStorage.getItem("aiCanDrawChatDiagram") === "true" });
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const getChatSubjectDescriptionDetailAsStreamUrl = () => {
  return `${connection.base.ai.url}${endpoints.ai["subject-description-chat"].context}${endpoints.ai["subject-description-chat"]["detail"]["as-stream"].context}`;
};

export const chatSubjectDescriptionDetailAsStream = async (
  chatParams,
): Promise<ApiResponse> => {
  const url = getChatSubjectDescriptionDetailAsStreamUrl();
  try {
    const response = await axios.post(url, { ...chatParams, supportsDiagram: localStorage.getItem("aiCanDrawChatDiagram") === "true" });
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const getChatBranchAssignmentPerformanceAsStreamUrl = () => {
  return `${connection.base.ai.url}${endpoints.ai["branch-assignment-performance-chat"].context}${endpoints.ai["branch-assignment-performance-chat"]["as-stream"].context}`;
};

export const chatBranchAssignmentPerformanceAsStream = async (
  chatParams,
): Promise<ApiResponse> => {
  const url = getChatBranchAssignmentPerformanceAsStreamUrl();
  try {
    const response = await axios.post(url, chatParams);
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryCorporateSubjectDescriptionActivityParamsByParams = async (
  queryParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.ai.url}${endpoints.ai["subject-description-chat"].context}${endpoints.ai["subject-description-chat"]["detail"]["activity"].context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const convertActivityTemplateChatTextToSpeechWithAI = async (
  ttsParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.ai.url}${endpoints.ai["activity-template-chat"].context}${endpoints.ai["activity-template-chat"]["tts"].context}`,
      ttsParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const convertSubjectDescriptionChatTextToSpeechWithAI = async (
  ttsParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.ai.url}${endpoints.ai["subject-description-chat"].context}${endpoints.ai["subject-description-chat"]["tts"].context}`,
      ttsParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const convertActivityTemplateChatSpeechToTextWithAI = async (
  ttsParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.ai.url}${endpoints.ai["activity-template-chat"].context}${endpoints.ai["activity-template-chat"]["stt"].context}`,
      ttsParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const convertSubjectDescriptionChatSpeechToTextWithAI = async (
  ttsParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.ai.url}${endpoints.ai["subject-description-chat"].context}${endpoints.ai["subject-description-chat"]["stt"].context}`,
      ttsParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Render an inline chat diagram from the tutor's [DIAGRAM: <description>] marker. The backend
 * writes the TikZ and compiles it to a PNG; params carry { description, dpi? }.
 */
export const generateDiagramFromDescription = async (
  diagramParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.ai.url}${endpoints.ai["diagram"].context}${endpoints.ai["diagram"]["from-description"].context}`,
      diagramParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * The signed-in profile's own AI credit standing — what it has spent against its role's pool in each
 * anchored window, and when a spent window frees.
 *
 * No parameters, deliberately: the backend answers about the caller in the Authorization header and
 * nothing else. `TOKEN_READ` is in the baseline bundle every profile holds, so a learner may ask about
 * their own budget.
 *
 * Used only to explain a refusal that already happened — the 423 itself cannot say when the pool frees,
 * because its body is the feature's error key and no Retry-After is sent. It is a single indexed
 * aggregate, not a generation, so it must never be given a long AI timeout.
 */
/**
 * Bounded on purpose. The caller treats "reading the status" as an absorbing state — it will not ask
 * again while a read is in flight — so a socket held open by a stalled proxy would silence every
 * further refusal for as long as it lasted. This is a single indexed aggregate; if it has not answered
 * in fifteen seconds it is not going to.
 */
export const AI_CREDIT_STATUS_TIMEOUT_MS = 15000;

export const retrieveAICreditStatus = async (): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.ai.url}${endpoints.ai["credits"].context}${endpoints.ai["credits"]["status"].context}`,
      { timeout: AI_CREDIT_STATUS_TIMEOUT_MS },
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};
